import { cacheData, loadData } from '~app/actions/cache';

import defined from '~app/core/defined';
import LocationInfo from '~app/core/locationInfo';
import fetchResource from '~app/core/fetchResource';
import Logging from '~app/core/logging';
import preloadImage from '~app/core/preloadImage';

import BiomeConfig from '~app/resources/data/biome-db-info';

const log = Logging.getLogger('app.actions.database');

export const LOAD_CELL_ID_MAP = 'LOAD_CELL_ID_MAP';

export const LOADED_IMAGE_SHEETS = 'LOADED_IMAGE_SHEETS';

// Cell id map

export function loadCellIdMap(updateProgress) {
  return async (dispatch) => {
    const cellIdMap = await loadDatabaseFile('cells.json');
    dispatch({
      type: LOAD_CELL_ID_MAP,
      cellIdMap,
    });
    updateProgress();
  };
}

export function countCellIdMaps() {
  return 1;
}

// Location info

export function loadLocationInfo(location) {
  const startTime = new Date();
  return async (dispatch) => {
    let content = null;

    const cellId = dispatch(computeCellId(location));
    if (defined(cellId)) {
      log.trace(`Loading cellId: ${cellId}`);

      content = dispatch(loadData('cell-data', cellId));
      if (!defined(content)) {
        content = await loadCell(cellId);
        dispatch(cacheData('cell-data', cellId, content));
      }

      const elapsedMs = (new Date() - startTime);
      log.trace(`Time to load location ${location.displayName}: ${elapsedMs} ms`);
    }

    return new LocationInfo(location, content);
  };
}

// Cell Id

function computeCellId(location) {
  return (dispatch, getState) => {
    const state = getState();
    const cellIdMap = state.database.cellIdMap;

    let cellId = null;
    const cell = _cellIdBinarySearch(location, cellIdMap);
    if (defined(cell)) {
      cellId = cell[0];
    }

    return cellId;
  };
}

// This function is adapted from:
// https://oli.me.uk/2013/06/08/searching-javascript-arrays-with-a-binary-search/
function _cellIdBinarySearch(location, cellIdMap) {
  // Extract the target coordinates.
  const [lon, lat] = location.degreeCoordinates();

  let minIndex = 0;
  let maxIndex = cellIdMap.length - 1;
  let currentIndex = undefined;
  let currentCell = undefined;

  while (minIndex <= maxIndex) {
      currentIndex = (minIndex + maxIndex) / 2 | 0;
      currentCell = cellIdMap[currentIndex];

      const comparison = _compareLocationToCell(lon, lat, currentCell);
      if (comparison < 0) {
        maxIndex = currentIndex - 1;
      }
      else if (comparison > 0) {
        minIndex = currentIndex + 1;
      }
      else {
          return currentCell;
      }
  }

  return null;
}

function _compareLocationToCell(lon, lat, cell) {
  // The array format is defined in geodb.js.
  const [minLat, minLon, maxLat, maxLon] = cell.slice(1);

  let comparison = 0;
  if (lat < minLat) {
    comparison = -1;
  }
  else if (lat > maxLat) {
    comparison = 1;
  }
  else if (lon < minLon) {
    comparison = -1;
  }
  else if (lon > maxLon) {
    comparison = 1;
  }

  return comparison;
}

// Cell

async function loadCell(cellId) {
  let content = null;

  const cellData = await loadDatabaseFile(`cells/${cellId}.json`);
  const biomeData = loadBiomeData(cellData.biome);
  const hotspotData = await loadHotspotData(cellData.hotspot);

  content = {
    cellData,
    biomeData,
    hotspotData,
  };

  return content;
}

function loadBiomeData(biomeId) {
  let biomeData = null;

  // The database uses 1-based indices for the biome ids.
  const biomeIndex = biomeId - 1;
  if (biomeIndex >= 0 && biomeIndex < BiomeConfig.length) {
    biomeData = BiomeConfig[biomeIndex];
  }
  else {
    log.error(`Failed to load biome config for id: ${biomeId}`);
  }
  return biomeData;
}

async function loadHotspotData(hotspotId) {
  let content = null;

  const hotspotData = await loadDatabaseFile(`hotspots/${hotspotId}.json`);

  // It seems possible to have a null hotspot data if you click the polar ice
  // area in Iceland.
  if (defined(hotspotData)) {
    let bannerImage = null;
    let wideBannerImage = null;
    let carouselImages = [];
    for (const imageDef of hotspotData.images) {
      if (imageDef.category === 'banner') {
        bannerImage = imageDef;
      }
      else if (imageDef.category === 'wbanner') {
        wideBannerImage = imageDef;
      }
      else if (imageDef.category === 'carousel') {
        carouselImages.push(imageDef);
      }
    }
    carouselImages.sort((a, b) => (a.carousel_order - b.carousel_order));

    const allImageDefs = [bannerImage, wideBannerImage, ...carouselImages];
    const loadedImages = await Promise.all(
      allImageDefs.map(imageDef => loadDatabaseImage(`${imageDef.key}.jpg`))
    );
    const allLoadedImageDefs = allImageDefs.map((imageDef, index) =>
      imageDef &&
      {
        image: loadedImages[index],
        credit: imageDef.credit,
      }
    );

    content = {
      description: hotspotData.description,
      bannerImageDef: allLoadedImageDefs[0],
      wideBannerImageDef: allLoadedImageDefs[1],
      carouselImageDefs: allLoadedImageDefs.slice(2),
    };
  }

  return content;
}

// Wildlife

export function loadWildlifeThumbnails(updateProgress) {
  return async (dispatch) => {
    const imageIndices = [0, 1, 2, 3];
    const imageSheets = await Promise.all(
      imageIndices.map(index =>
        loadDatabaseImage(`wildlife-thumbnails-${index}.jpg`, true)
        .then(result => {
          updateProgress();
          return result;
        })
      )
    );

    dispatch({
      type: LOADED_IMAGE_SHEETS,
      imageSheets,
    });
  };
}

export function countWildlifeThumbnailSheets() {
  return 4;
}

export function loadSpeciesInfo(binomial) {
  return async (dispatch) => {
    let content = dispatch(loadData('wildlife-species', binomial));

    if (!defined(content)) {
      content = await loadDatabaseFile(`wildlife/${binomial}.json`);
      dispatch(cacheData('wildlife-species', binomial, content));
    }

    return content;
  };
}

// Server communication

async function loadDatabaseFile(file) {
  const response = await fetchResource(`geodb/${file}`);
  const json = await response.json();

  return json;
}

async function loadDatabaseImage(file, preload=false) {
  const response = await fetchResource(`geodb/media/${file}`);
  let result = await response.blob();

  if (preload) {
    result = result ? URL.createObjectURL(result) : null;
    await preloadImage(result);
  }

  // Result can either be a blob (if we are not preloading)
  // or an object URL (if we are preloading).
  return result;
}



// WEBPACK FOOTER //
// ./src/actions/database.js